import classnames from 'classnames';
import { Message, MessageAttachedFile } from 'lib-shared-legacy-types';

import { PrivateMediaPlayer } from 'components/privateMediaPlayer';
import Image, { ImageProps } from 'components/shared/Image/Image';
import { actions } from 'components/shared/attachmentsPreviewModal/actions/attachmentsPreviewModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { AttachedFile } from '../AttachedFile';

import styles from './attachment.module.scss';

const { attachmentsPreviewModalOpen } = actions;

/** Represents the attachment types. */
export type AttachmentType = 'image' | 'video' | 'audio';

type AttachmentOptions = {
  /** Whether to scale the images. */
  scaleImage?: boolean;
};

/** The recognized attachment types along with their extensions. */
const EXTENSIONS: Record<AttachmentType, string[]> = {
  image: ['jpg', 'jpeg', 'bmp', 'gif', 'tiff', 'png', 'webp'],
  video: ['mp4', 'm4v', 'mpeg', 'mpg', 'mov', 'avi', 'wmv', 'webm', 'flv'],
  audio: [
    'aac',
    'wav',
    'mp3',
    'aiff',
    'acc',
    'ogg',
    'oga',
    'wma',
    'flac',
    'alac',
  ],
};

/**
 * Gets the attachment type for the attachment.
 *
 * @param attachment The attachment.
 */
export function getAttachmentType(
  attachment: MessageAttachedFile,
): AttachmentType | undefined {
  const filename = attachment.pretty_name || '';
  const extension = filename.substring(filename.lastIndexOf('.') + 1);

  return Object.keys(EXTENSIONS).find((mimeCategory) =>
    EXTENSIONS[mimeCategory as AttachmentType].includes(extension),
  ) as AttachmentType;
}

const ImageWrapper = (props: ImageProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const handleImageClick = (src?: string) => {
    if (src) {
      dispatch(
        attachmentsPreviewModalOpen({
          fileName: 'inlined.jpg',
          url: src,
        }),
      );
    }
  };

  return <Image {...props} onClick={handleImageClick} />;
};

function getSingleAttachment(
  attachment: MessageAttachedFile,
  options?: AttachmentOptions,
): JSX.Element {
  const url = attachment.url;
  const type = getAttachmentType(attachment);

  switch (type) {
    case 'image':
      return (
        <ImageWrapper
          wrapperClassName={classnames(styles.imageWrapper, {
            [styles.scaled]: options?.scaleImage,
          })}
          className={classnames(styles.image, {
            [styles.scaled]: options?.scaleImage,
          })}
          src={url}
          speed="1"
          alt="Image attachment"
        />
      );
    case 'video':
    case 'audio':
      return (
        <PrivateMediaPlayer
          mediaFile={{ src: url, type }}
          filename={attachment.pretty_name}
        />
      );
    default:
      return <AttachedFile fileUrl={url} prettyName={attachment.pretty_name} />;
  }
}

/**
 * Gets the first attachment component based on the attachment type.
 *
 * @param message The message.
 */
export function getAttachment(
  message: Message,
  options?: AttachmentOptions,
): JSX.Element | null {
  if (!message.attached_files) {
    return null;
  }

  const attachment = Array.isArray(message.attached_files)
    ? message.attached_files[0]
    : message.attached_files;

  return getSingleAttachment(attachment, options);
}

/**
 * Gets all the attachment component based on the attachment type.
 *
 * @param message The message.
 */
export function getAttachments(
  message: Message,
  options?: AttachmentOptions,
): Array<JSX.Element> {
  if (!message.attached_files) {
    return [];
  }

  const attachments = Array.isArray(message.attached_files)
    ? message.attached_files
    : [message.attached_files];

  return attachments.map((attachment) => {
    return getSingleAttachment(attachment, options);
  });
}
